import { Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	AcceptanceStatus,
	GetKYCInvestorInstanceRes,
	KYCFileType,
	SubjectAction,
} from '@dealstack/shared/lib/generated';
import { getDecisionAudit } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/utils/getDecisionAudit';
import { Step } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { KycActionButtons } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/component/KycActionButtons';
import { reviewKycForm } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	SelectedTemplate,
	isAwaitingRequiredSignature,
	isFormSubmitted,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/types';
import { DecisionCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/DecisionCard';
import { RejectionConfirmation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/RejectionConfirmation';
import { useRejectionState } from '@dealstack/frontend/shared/hooks/useRejectionState';
import { useManageKYCController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/useManageKYCController';

interface Props {
	form: SelectedTemplate;
	currentStep: Step;
	currentInvestor: GetKYCInvestorInstanceRes | undefined;
	isReadOnly?: boolean;
}

export const ReviewKycForm = ({
	form,
	currentStep,
	currentInvestor,
	isReadOnly = false,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? '';
	const queryClient = useQueryClient();
	const { getKYCDataRequest } = useManageKYCController(currentInvestor?.id, kycWorkflowId);

	// Same audit source as files/declarations: the KYC event log (REVIEWED events), scoped to this
	// form by templateId, surfaces who approved/rejected it and when in the DecisionCard.
	const audit = useMemo(() => {
		const events = currentInvestor?.kycHistory[KYCFileType.FORM] ?? [];
		const scoped = events.filter((event) => event.templateId === form.templateId);
		return getDecisionAudit(scoped);
	}, [currentInvestor, form.templateId]);

	const mutation = useMutation({
		mutationFn: (vars: { status: AcceptanceStatus; rejectionComment?: string }) =>
			reviewKycForm({
				dealId: dealId ?? '',
				programmeId: mipId ?? '',
				kycWorkflowId,
				templateId: form.templateId,
				status: vars.status,
				rejectionComment: vars.rejectionComment,
			}),
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({
					queryKey: [
						API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_INVESTOR_FORMS_GET,
					],
				}),
				queryClient.invalidateQueries({
					queryKey: [
						API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_GET,
					],
				}),
				getKYCDataRequest.refetch(),
			]);
		},
	});

	const isLoading = mutation.isPending;
	const isInherited = form.subjectAction === SubjectAction.NOT_YET_ACTED;
	// A warning, not a block: approving an already-executed document is the manager's call, and
	// the approval closes the outstanding signature request server-side.
	const awaitingSignature = isAwaitingRequiredSignature(form);

	const {
		isRejecting,
		comment,
		commentError,
		handleCommentChange,
		handleConfirmReject,
		handleCancelReject,
		handleDecline,
		reset,
	} = useRejectionState({
		emptyCommentError: tMipManage('kyc.review.add_comment_error'),
		onConfirm: async (rejectionComment) => {
			await mutation.mutateAsync({ status: AcceptanceStatus.REREQUESTED, rejectionComment });
			reset();
		},
	});

	if (!isFormSubmitted(form)) {
		return (
			<Text size="B6" c="dark.2">
				{tMipManage('kyc.review.no_documents')}
			</Text>
		);
	}

	const isApproved = form.status === AcceptanceStatus.APPROVED;
	const isRejected =
		form.status === AcceptanceStatus.REJECTED || form.status === AcceptanceStatus.REREQUESTED;

	if (isApproved || isRejected) {
		return (
			<DecisionCard
				status={form.status ?? AcceptanceStatus.PENDING}
				auditedBy={audit.auditedBy}
				timestamp={audit.timestamp}
				rejectionComment={form.rejectionComment}
				onUpdateDecision={async () => {
					await mutation.mutateAsync({
						status: AcceptanceStatus.PENDING,
						rejectionComment: '',
					});
					reset();
				}}
				isLoading={isLoading}
				disabled={isReadOnly || isInherited}
			/>
		);
	}

	const handleApprove = async () => {
		await mutation.mutateAsync({ status: AcceptanceStatus.APPROVED, rejectionComment: '' });
		reset();
	};

	return (
		<Flex direction="column" gap="1rem">
			<KycActionButtons
				currentInvestor={currentInvestor}
				currentStep={currentStep}
				selectedKycFile={undefined}
				loading={isLoading}
				isDisabled={isReadOnly || isInherited}
				onApprove={handleApprove}
				onDecline={handleDecline}
				approvalStatusOverride={form.status ?? AcceptanceStatus.PENDING}
			/>
			{isInherited && (
				<Flex align="center" gap="0.5rem" c="dark.2">
					<Icon variant="clock-time-watch" />
					<Text size="B6">{tMipManage('kyc.review.inherited_file_hint')}</Text>
				</Flex>
			)}
			{awaitingSignature && (
				<Flex
					align="center"
					gap="0.5rem"
					c="orange.6"
					data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_KYC_REVIEW_AWAITING_SIGNATURE_HINT}
				>
					<Icon variant="clock-time-watch" />
					<Text size="B6">{tMipManage('kyc.review.awaiting_signature_hint')}</Text>
				</Flex>
			)}
			{isRejecting && !isReadOnly && (
				<RejectionConfirmation
					comment={comment}
					commentError={commentError}
					isLoading={isLoading}
					disabled={isInherited}
					onCommentChange={handleCommentChange}
					onCancel={handleCancelReject}
					onConfirm={handleConfirmReject}
				/>
			)}
		</Flex>
	);
};
